import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import theme from '../../../../theme/theme';
import {
  CATALOGUE_MODULES,
  PERMISSION_ACTIONS,
  type CatalogueLine,
  type CatalogueModule,
  type LineId,
  type LinePermissions,
  type PermissionAction,
  type PermissionMatrix as PermissionMatrixValue,
} from '../../types/rights-profile.types';
import { MODULE_LABEL_KEY } from '../../constants/catalogue-labels';
import {
  toggleAction,
  applyModulePreset,
  countModuleGrants,
  type ModulePreset,
} from '../../logic/permissionMatrix.logic';
import { PermissionMatrixRow } from './PermissionMatrixRow';

interface PermissionMatrixProps {
  /** The permission catalogue from `GET /rights-profiles/catalogue` (display order). */
  catalogue: CatalogueLine[];
  value: PermissionMatrixValue;
  onChange: (updated: PermissionMatrixValue) => void;
  readOnly?: boolean;
  /**
   * Visual variant (HOMCO-326 follow-up). `'default'` = the create look (grey header,
   * white body). `'form'` matches the maquette: the matrix body sits on
   * `background.page` and the "Fiches et actions" header on `background.section`.
   */
  variant?: 'default' | 'form';
}

const EMPTY_PERMS: LinePermissions = {};

// Section header bar: white by default, subtle grey/blue tint (primary at low
// opacity, the app's idiom) on hover only. The inner table rows have no hover.
const SECTION_HEADER_HOVER_BG = alpha(theme.palette.primary.main, 0.1);

// Compact, low-weight preset buttons so they don't dominate the section header.
const PRESET_BUTTON_SX = {
  textTransform: 'none',
  minWidth: 0,
  py: 0.1,
  px: 1,
  fontSize: theme.typography.fontSizeLabel,
  lineHeight: 1.6,
} as const;

/** Catalogue lines grouped by module, in `CATALOGUE_MODULES` display order. */
function groupByModule(catalogue: CatalogueLine[]): [CatalogueModule, CatalogueLine[]][] {
  return CATALOGUE_MODULES.map((module): [CatalogueModule, CatalogueLine[]] => [
    module,
    catalogue.filter(line => line.module === module),
  ]).filter(([, lines]) => lines.length > 0);
}

const allExpanded = (modules: CatalogueModule[], open: boolean): Record<string, boolean> =>
  Object.fromEntries(modules.map(m => [m, open]));

/**
 * Permission matrix editor: one accordion per module, one row per catalogue line,
 * one column per action. The « no phantom right » consistency rules and per-module
 * presets live in `permissionMatrix.logic`; this component only renders and
 * dispatches new matrix values through `onChange` (fully controlled). It reads the
 * catalogue — it never recopies it.
 */
export function PermissionMatrix({
  catalogue,
  value,
  onChange,
  readOnly,
  variant = 'default',
}: PermissionMatrixProps) {
  const { t } = useTranslation('rightsProfiles');
  // `form` (maquette): body on the page surface, header on the section surface.
  const isForm = variant === 'form';
  const surfaceBg = isForm ? theme.palette.background.page : theme.palette.background.paper;
  const headerBg = isForm ? theme.palette.background.section : theme.palette.grey[50];
  const groups = useMemo(() => groupByModule(catalogue), [catalogue]);
  const modules = useMemo(() => groups.map(([module]) => module), [groups]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    allExpanded(modules, true),
  );

  const handleToggle = (line: LineId) => (action: PermissionAction, checked: boolean) =>
    onChange(toggleAction(catalogue, value, line, action, checked));

  const handlePreset = (module: CatalogueModule, preset: ModulePreset) =>
    onChange(applyModulePreset(catalogue, value, module, preset));

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
        overflow: 'hidden',
        bgcolor: surfaceBg,
      }}
    >
      {/* Header: intro text + expand/collapse, kept together as one block. */}
      <Box
        sx={{
          p: 2,
          bgcolor: headerBg,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography sx={{ fontWeight: 600, color: theme.palette.text.body }}>
          {t('matrix.introTitle')}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button size="small" onClick={() => setExpanded(allExpanded(modules, true))}>
            {t('matrix.expandAll')}
          </Button>
          <Button size="small" onClick={() => setExpanded(allExpanded(modules, false))}>
            {t('matrix.collapseAll')}
          </Button>
        </Stack>
      </Box>

      {groups.map(([module, lines], index) => {
        const { granted, total } = countModuleGrants(catalogue, value, module);
        return (
          <Accordion
            key={module}
            expanded={expanded[module] ?? true}
            onChange={(_event, isExpanded) =>
              setExpanded(state => ({ ...state, [module]: isExpanded }))
            }
            disableGutters
            elevation={0}
            sx={{
              // Transparent so the container surface (page bg in form) shows through,
              // instead of the Accordion's default paper (white).
              bgcolor: 'transparent',
              borderTop: index > 0 ? `1px solid ${theme.palette.divider}` : 'none',
              '&:before': { display: 'none' },
            }}
          >
            {/*
              Shared flex header, clickable as a whole: clicking anywhere on the bar
              (name, chevron, counter, empty space) folds/unfolds the section via the
              Box onClick. The AccordionSummary (chevron + name) keeps the accessible
              button + aria-expanded and stops propagation so it toggles only once; the
              preset Buttons stop propagation so a preset click never folds the section.
              Presets/counter stay OUTSIDE the summary's native <button> (nested
              interactive controls break button semantics / a11y). MUI renders the first
              Accordion child as the summary (outside the collapse), so this whole Box is
              the header and AccordionDetails stays the panel.
            */}
            <Box
              onClick={() =>
                setExpanded(state => ({ ...state, [module]: !(state[module] ?? true) }))
              }
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                pl: 1,
                pr: 2,
                gap: 1.5,
                cursor: 'pointer',
                // The module header bar stays white even when the matrix body sits on
                // the page surface (form variant); only the inner table shows it.
                backgroundColor: theme.palette.background.paper,
                '&:hover': { backgroundColor: SECTION_HEADER_HOVER_BG },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                onClick={event => event.stopPropagation()}
                sx={{
                  // width:auto overrides MUI's default width:100% so the summary wraps
                  // just the chevron + name; row-reverse puts the chevron left of the name.
                  width: 'auto',
                  flexShrink: 0,
                  flexDirection: 'row-reverse',
                  gap: 1,
                  '& .MuiAccordionSummary-content': { flexGrow: 0, my: 0 },
                }}
              >
                <Typography sx={{ fontWeight: 600, color: theme.palette.text.body }}>
                  {t(MODULE_LABEL_KEY(module))}
                </Typography>
              </AccordionSummary>
              {!readOnly && (
                <Stack
                  direction="row"
                  spacing={1}
                  onClick={event => event.stopPropagation()}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    sx={PRESET_BUTTON_SX}
                    onClick={() => handlePreset(module, 'readOnly')}
                  >
                    {t('matrix.presets.readOnly')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={PRESET_BUTTON_SX}
                    onClick={() => handlePreset(module, 'fullAccess')}
                  >
                    {t('matrix.presets.fullAccess')}
                  </Button>
                </Stack>
              )}
              <Typography
                sx={{
                  ml: 'auto',
                  mr: 3,
                  fontSize: theme.typography.fontSizeDescription,
                  color: theme.palette.text.subtle,
                }}
              >
                {t('matrix.grantCount', { granted, total })}
              </Typography>
            </Box>
            <AccordionDetails sx={{ pt: 0, bgcolor: 'rgb(241 244 248)' }}>
              <Table
                size="small"
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: headerBg }}>
                    <TableCell />
                    {PERMISSION_ACTIONS.map(action => (
                      <TableCell
                        key={action}
                        align="center"
                        sx={{ color: theme.palette.text.body }}
                      >
                        {t(`actions.${action}`)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map(catalogueLine => (
                    <PermissionMatrixRow
                      key={catalogueLine.line}
                      catalogueLine={catalogueLine}
                      perms={value[catalogueLine.line] ?? EMPTY_PERMS}
                      readOnly={readOnly}
                      onToggle={handleToggle(catalogueLine.line)}
                    />
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
